import { interactionState, pan } from './viewportState.js';
import type { EdgeRef } from '../editor/connectionMutations.js';
import { edgeIntersectsCut } from './edgeMath.js';
import { edgeKey, type EdgeVisualStateController } from './edgeVisualState.js';

interface Point {
  x: number;
  y: number;
}

interface CutGestureDeps {
  clearSnapHighlights: () => void;
  getEdges: () => EdgeRef[];
  getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point;
  visualState: EdgeVisualStateController;
  hasPendingConnection: () => boolean;
  refreshRenderedConnections: () => void;
  severEdgesIntersectingLine: (start: Point, end: Point) => number;
}

export function initCutGesture(graphArea: HTMLElement, deps: CutGestureDeps): { hasActive: () => boolean; clear: () => void } {
  let cutGesture: { start: Point; current: Point } | null = null;

  const eventToWorldPoint = (clientX: number, clientY: number): Point => {
    const rect = graphArea.getBoundingClientRect();
    return {
      x: (clientX - rect.left - pan.x) / pan.scale,
      y: (clientY - rect.top - pan.y) / pan.scale
    };
  };

  const renderCutGesture = (): void => {
    const svg = document.getElementById('conn-svg') as unknown as SVGSVGElement;
    svg.querySelectorAll('.edge-cut-line').forEach(el => el.remove());
    document.body.classList.toggle('cutting-edges', !!cutGesture);
    deps.visualState.setCuttingEdges(!!cutGesture);
    if (!cutGesture) return;

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('class', 'edge-cut-line');
    line.setAttribute('x1', String(cutGesture.start.x));
    line.setAttribute('y1', String(cutGesture.start.y));
    line.setAttribute('x2', String(cutGesture.current.x));
    line.setAttribute('y2', String(cutGesture.current.y));
    svg.appendChild(line);
  };

  const clear = (): void => {
    cutGesture = null;
    deps.visualState.clearCutPreviewKeys();
    renderCutGesture();
    deps.refreshRenderedConnections();
  };

  const updateCutPreview = (): void => {
    if (!cutGesture) return;
    const keys = deps.getEdges()
      .filter(edge => {
        const from = deps.getDotWorldPos(edge.fromNode, edge.fromPort, true);
        const to = deps.getDotWorldPos(edge.toNode, edge.toPort, false);
        return edgeIntersectsCut(from, to, cutGesture!.start, cutGesture!.current);
      })
      .map(edge => edgeKey(edge.fromNode, edge.fromPort, edge.toNode, edge.toPort));
    deps.visualState.setCutPreviewKeys(keys);
    deps.refreshRenderedConnections();
  };

  graphArea.addEventListener('mousedown', e => {
    if (e.button !== 0 || !e.ctrlKey || deps.hasPendingConnection() || interactionState.draggingNode) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = eventToWorldPoint(e.clientX, e.clientY);
    cutGesture = { start: pos, current: pos };
    deps.clearSnapHighlights();
    renderCutGesture();
    updateCutPreview();
  });

  window.addEventListener('mousemove', e => {
    if (!cutGesture) return;
    cutGesture.current = eventToWorldPoint(e.clientX, e.clientY);
    renderCutGesture();
    updateCutPreview();
  });

  window.addEventListener('mouseup', e => {
    if (!cutGesture) return;
    cutGesture.current = eventToWorldPoint(e.clientX, e.clientY);
    const dist = Math.hypot(cutGesture.current.x - cutGesture.start.x, cutGesture.current.y - cutGesture.start.y);
    if (dist > 2) deps.severEdgesIntersectingLine(cutGesture.start, cutGesture.current);
    clear();
  });

  return {
    hasActive: () => !!cutGesture,
    clear
  };
}
